import { useEffect, useMemo, useRef, type RefObject } from 'react';
import { Button } from '@/components/ui/button';
import { usePrefetchProspectDetail } from '@/hooks/queries';
import { layerOpen, typing } from './useSelectionKeys';

const count = new Intl.NumberFormat('en-US');

export interface Steps {
  /** Where the open person sits in the list; -1 when the list doesn't hold them (filtered out, or removed). */
  index: number;
  previous: string | undefined;
  next: string | undefined;
}

/**
 * Where one person sits in a list's order — the book's filtered rows or a call book's lines — and who is
 * either side of them. The ids are indexed once per list, so stepping through a large book stays cheap.
 */
export function useSteps(ids: readonly string[], current: string): Steps {
  const positions = useMemo(() => new Map(ids.map((id, index) => [id, index])), [ids]);
  const index = positions.get(current) ?? -1;
  return {
    index,
    previous: index > 0 ? ids[index - 1] : undefined,
    next: index >= 0 && index < ids.length - 1 ? ids[index + 1] : undefined,
  };
}

/**
 * Read the people either side ahead of time, so J / K (or the step buttons) show them at once. A read still fresh
 * isn't repeated, so stepping back and forth costs nothing more.
 */
export function usePrefetchSteps({ previous, next }: Pick<Steps, 'previous' | 'next'>) {
  const prefetch = usePrefetchProspectDetail();
  useEffect(() => {
    if (previous) void prefetch(previous);
    if (next) void prefetch(next);
  }, [previous, next, prefetch]);
}

/**
 * J moves to the next person, K to the previous — never while typing, and never while a menu or dialog
 * other than `within` (the profile's own dialog, when it is one) is open.
 */
export function useStepKeys({ previous, next, onStep, within }: Omit<Steps, 'index'> & { onStep: (id: string) => void; within?: RefObject<HTMLElement | null> }) {
  // Read at key time, so the listener is added once rather than on every step.
  const latest = useRef({ previous, next, onStep, within });
  latest.current = { previous, next, onStep, within };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const now = latest.current;
      if (e.metaKey || e.ctrlKey || e.altKey || typing(e.target) || layerOpen(now.within?.current)) return;
      const key = e.key.toLowerCase();
      const target = key === 'j' ? now.next : key === 'k' ? now.previous : undefined;
      if (!target) return;
      e.preventDefault();
      now.onStep(target);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}

/** "2 of 412" and the ↑ K / ↓ J buttons, stopping at the ends of the list. */
export function ProspectSteps({ steps: { index, previous, next }, total, onStep }: { steps: Steps; total: number; onStep: (id: string) => void }) {
  return (
    <div className="flex items-center gap-2">
      {index >= 0 && (
        <span className="text-xs tabular-nums text-muted-foreground">
          {count.format(index + 1)} of {count.format(total)}
        </span>
      )}
      <Button variant="outline" className="h-10" disabled={!previous} onClick={() => previous && onStep(previous)} aria-label="Previous person (K)">
        ↑ K
      </Button>
      <Button variant="outline" className="h-10" disabled={!next} onClick={() => next && onStep(next)} aria-label="Next person (J)">
        ↓ J
      </Button>
    </div>
  );
}
